// ─── UniResolve Dashboard Design System ──────────────────────────────────────

export const FONT = "'Inter', 'Segoe UI', system-ui, sans-serif";

export const THEMES = {
  dark: {
    name: 'dark',
    bg:           '#0d0d14',
    bgSecondary:  '#12121e',
    sidebar:      'rgba(10,10,20,0.97)',
    topbar:       'rgba(10,10,20,0.92)',
    card:         'rgba(255,255,255,0.045)',
    cardHover:    'rgba(255,255,255,0.075)',
    border:       'rgba(255,255,255,0.07)',
    borderHover:  'rgba(99,102,241,0.4)',
    text:         '#f1f1f5',
    textSub:      'rgba(255,255,255,0.55)',
    textMuted:    'rgba(255,255,255,0.28)',
    accent:       '#6366f1',
    accentGrad:   'linear-gradient(135deg,#6366f1 0%,#8b5cf6 100%)',
    scrollThumb:  'rgba(99,102,241,0.35)',
    inputBg:      'rgba(255,255,255,0.06)',
    tableRow:     'rgba(99,102,241,0.05)',
  },
};

export const COLORS = {
  purple : '#8b5cf6',
  violet : '#7c3aed',
  cyan   : '#06b6d4',
  green  : '#10b981',
  amber  : '#f59e0b',
  red    : '#ef4444',
  orange : '#f97316',
  blue   : '#3b82f6',
  pink   : '#ec4899',
  teal   : '#14b8a6',
};

export const STAT_GRADIENTS = {
  blue   : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  green  : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  orange : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
  purple : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
  pink   : 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
  amber  : 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
  cyan   : 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
};

export const STATUS_COLOR = {
  pending   : '#f59e0b',
  verified  : '#3b82f6',
  resolved  : '#10b981',
  rejected  : '#ef4444',
  escalated : '#f97316',
};
export const STATUS_BG = Object.fromEntries(
  Object.entries(STATUS_COLOR).map(([k, v]) => [k, v + '30'])
);

let _injected = false;
export function injectDashStyles() {
  if (_injected || typeof document === 'undefined') return;
  _injected = true;
  const s = document.createElement('style');
  s.id = 'ur-dash-styles';
  s.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: ${FONT}; }

    @keyframes fadeUp    { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
    @keyframes fadeIn    { from{opacity:0} to{opacity:1} }
    @keyframes scaleIn   { from{opacity:0;transform:scale(0.93)} to{opacity:1;transform:scale(1)} }
    @keyframes slideDown { from{opacity:0;transform:translateY(-12px)} to{opacity:1;transform:translateY(0)} }
    @keyframes slideInUp { from{opacity:0;transform:translateY(30px)} to{opacity:1;transform:translateY(0)} }
    @keyframes spin      { to{transform:rotate(360deg)} }
    @keyframes sideSlide { from{transform:translateX(-100%)} to{transform:translateX(0)} }
    @keyframes blockPop  { 0%{opacity:0;transform:scale(0.92) translateY(10px)} 100%{opacity:1;transform:scale(1) translateY(0)} }
    @keyframes orbDrift  { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(30px,-20px) scale(1.05)} 66%{transform:translate(-20px,15px) scale(0.97)} }
    @keyframes gridPulse { 0%,100%{opacity:0.3} 50%{opacity:0.6} }
    @keyframes livePulse { 0%,100%{box-shadow:0 0 0 0 rgba(16,185,129,0.5)} 50%{box-shadow:0 0 0 6px rgba(16,185,129,0)} }
    @keyframes countUp   { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
    @keyframes float     { 0%{transform:translateY(0px)} 50%{transform:translateY(-20px)} 100%{transform:translateY(0px)} }
    @keyframes pulse     { 0%{transform:scale(1)} 50%{transform:scale(1.05)} 100%{transform:scale(1)} }
    @keyframes bounce    { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-5px)} }
    @keyframes glow      { 0%,100%{opacity:0.5;transform:scale(1)} 50%{opacity:0.8;transform:scale(1.1)} }

    ::-webkit-scrollbar { width:4px; height:4px; }
    ::-webkit-scrollbar-track { background:transparent; }
    ::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.25); border-radius:2px; }
    ::-webkit-scrollbar-thumb:hover { background:rgba(255,255,255,0.4); }

    /* Ensure native select dropdown options are always readable */
    select option { background: #1e1b4b; color: #fff; }

    .ur-card-hover { transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease !important; }
    .ur-card-hover:hover { transform: translateY(-3px) !important; box-shadow: 0 16px 40px rgba(0,0,0,0.2) !important; }
    .ur-btn-hover { transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease !important; }
    .ur-btn-hover:hover { transform: translateY(-1px) !important; opacity: 0.92 !important; }
    .ur-btn-hover:active { transform: translateY(0) scale(0.98) !important; }
    .ur-row-hover { transition: background 0.15s ease !important; cursor: pointer; }
    .ur-row-hover:hover { background: rgba(255,255,255,0.08) !important; }
    .ur-nav-item { transition: all 0.18s ease !important; }
    .ur-nav-item:hover { background: rgba(255,255,255,0.12) !important; color: #fff !important; }
    .ur-action-block { animation: blockPop 0.4s cubic-bezier(0.34,1.56,0.64,1) both; }
    .ur-action-block:hover { transform: translateY(-4px) scale(1.04) !important; }

    /* ── Mobile responsive ── */
    @media (max-width: 768px) {
      .ur-sidebar { transform: translateX(-100%); transition: transform 0.3s ease !important; }
      .ur-sidebar.open { transform: translateX(0) !important; }
      .ur-sidebar-overlay { display: block !important; }
      .ur-main { margin-left: 0 !important; overflow-x: hidden !important; }
      .ur-topbar-title { font-size: 16px !important; }
      .ur-content { padding: 12px !important; }
      .ur-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
      .ur-hide-mobile { display: none !important; }
      .ur-grid4 { grid-template-columns: repeat(2, 1fr) !important; }
      .ur-grid3 { grid-template-columns: 1fr !important; }
      .ur-grid2 { grid-template-columns: 1fr !important; }
      /* Prevent horizontal overflow on mobile */
      .ur-card-hover { max-width: 100%; }
      table { font-size: 12px !important; }
      table th, table td { padding: 8px 10px !important; }
      /* Prevent any fixed-width element from causing scroll */
      body, #root { overflow-x: hidden !important; max-width: 100vw; }
    }
    @media (max-width: 480px) {
      .ur-grid4 { grid-template-columns: repeat(2, 1fr) !important; }
      .ur-topbar { padding: 10px 12px !important; }
      .ur-content { padding: 10px !important; }
      /* Stack topbar items on very small screens */
      .ur-topbar-actions { flex-wrap: wrap !important; gap: 6px !important; }
    }
  `;
  document.head.appendChild(s);
}

export function buildStyles(t) {
  const border = `1px solid ${t.border}`;

  return {
    page    : {
      minHeight: '100vh',
      background: t.bg,
      fontFamily: FONT, color: t.text, position: 'relative', overflow: 'hidden',
    },
    layout  : { display:'flex', minHeight:'100vh', position:'relative', zIndex:1, overflowX:'hidden' },

    // Background decorations
    bgGrid  : {
      position:'fixed', inset:0,
      backgroundImage: `linear-gradient(${t.border} 1px,transparent 1px),linear-gradient(90deg,${t.border} 1px,transparent 1px)`,
      backgroundSize:'48px 48px', zIndex:0, pointerEvents:'none', animation:'gridPulse 6s ease-in-out infinite',
    },
    bgOrb1  : {
      position:'fixed', width:500, height:500, borderRadius:'50%',
      background:'radial-gradient(circle,rgba(255,255,255,0.15) 0%,transparent 70%)',
      top:-150, right:-100, pointerEvents:'none', zIndex:0, animation:'orbDrift 18s ease-in-out infinite',
    },
    bgOrb2  : {
      position:'fixed', width:400, height:400, borderRadius:'50%',
      background:'radial-gradient(circle,rgba(255,255,255,0.1) 0%,transparent 70%)',
      bottom:-100, left:-80, pointerEvents:'none', zIndex:0, animation:'orbDrift 22s ease-in-out infinite reverse',
    },
    bgOrb3  : {
      position:'fixed', width:300, height:300, borderRadius:'50%',
      background:'radial-gradient(circle,rgba(255,255,255,0.08) 0%,transparent 70%)',
      top:'40%', left:'30%', pointerEvents:'none', zIndex:0, animation:'orbDrift 14s ease-in-out infinite 3s',
    },

    // Sidebar
    sidebar     : {
      width:248, background: t.sidebar,
      borderRight: border,
      display:'flex', flexDirection:'column', position:'fixed', top:0, left:0, height:'100vh',
      zIndex:100, backdropFilter:'blur(20px)',
      boxShadow: '4px 0 24px rgba(0,0,0,0.3)',
      transition:'transform 0.3s ease', animation:'sideSlide 0.3s ease-out',
    },
    sidebarHidden: { transform:'translateX(-100%)' },
    sidebarLogo : {
      padding:'22px 20px 18px',
      borderBottom: border,
      display:'flex', alignItems:'center', gap:12, cursor:'pointer',
    },
    logoMark    : {
      width:36, height:36, borderRadius:10,
      background:'linear-gradient(135deg,rgba(255,255,255,0.3),rgba(255,255,255,0.15))',
      border:'1px solid rgba(255,255,255,0.3)',
      display:'flex', alignItems:'center', justifyContent:'center',
      fontSize:15, fontWeight:900, color:'#fff', flexShrink:0,
      boxShadow:'0 4px 12px rgba(0,0,0,0.2)',
    },
    logoText    : { fontSize:16, fontWeight:800, color:'#fff', letterSpacing:'-0.3px' },
    logoSub     : { fontSize:10, color:'rgba(255,255,255,0.65)', textTransform:'uppercase', letterSpacing:1, marginTop:1 },
    navSection  : {
      padding:'18px 16px 6px', fontSize:10, fontWeight:700,
      color:'rgba(255,255,255,0.5)', textTransform:'uppercase', letterSpacing:1.2,
    },
    navItem     : {
      display:'flex', alignItems:'center', gap:10, padding:'9px 14px',
      borderRadius:9, margin:'1px 10px', cursor:'pointer', fontSize:13.5,
      fontWeight:500, color:'rgba(255,255,255,0.75)', position:'relative',
    },
    navActive   : {
      background:'rgba(255,255,255,0.18)',
      color:'#fff', fontWeight:700,
      boxShadow:'0 2px 8px rgba(0,0,0,0.1)',
    },
    navActiveDot: {
      position:'absolute', right:12, width:6, height:6,
      borderRadius:'50%', background:'#fff',
    },
    navIcon     : { width:20, textAlign:'center', fontSize:15, flexShrink:0 },
    sidebarFoot : {
      marginTop:'auto', padding:'14px',
      borderTop: border,
    },
    userChip    : {
      display:'flex', alignItems:'center', gap:10, padding:'10px 12px',
      borderRadius:10, background:'rgba(255,255,255,0.12)',
      border:'1px solid rgba(255,255,255,0.2)', marginBottom:10, cursor:'pointer',
    },
    avatar      : {
      width:34, height:34, borderRadius:9,
      background:'linear-gradient(135deg,rgba(255,255,255,0.3),rgba(255,255,255,0.15))',
      border:'1px solid rgba(255,255,255,0.3)',
      display:'flex', alignItems:'center', justifyContent:'center',
      fontSize:13, fontWeight:800, color:'#fff', flexShrink:0,
    },
    userName    : { fontSize:13, fontWeight:600, color:'#fff' },
    userRole    : { fontSize:11, color:'rgba(255,255,255,0.6)', marginTop:1 },

    // Topbar
    topbar      : {
      height: 'auto',
      background: t.topbar,
      borderBottom: border,
      display:'flex', flexDirection:'column', justifyContent:'center',
      padding:'16px 28px', position:'sticky', top:0, zIndex:50,
      backdropFilter:'blur(20px)', animation:'slideDown 0.4s ease-out',
      boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
    },
    topbarTitle : { fontSize:20, fontWeight:800, color:'#fff', letterSpacing:'-0.3px' },
    topbarSub   : { fontSize:12, color:'rgba(255,255,255,0.7)', marginTop:2 },

    main    : { flex:1, display:'flex', flexDirection:'column', minHeight:'100vh', position:'relative', zIndex:1, transition:'margin-left 0.3s ease', minWidth:0, overflow:'hidden', maxWidth:'100vw' },
    content : { padding:'24px', flex:1, minWidth:0, overflowX:'hidden' },

    // Cards
    card        : {
      background: t.card, border: border, borderRadius:14, padding:'20px',
      animation:'fadeUp 0.5s ease-out both', backdropFilter:'blur(12px)',
      boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
    },
    statCard    : {
      background: t.card, border: border, borderRadius:14, padding:'20px 18px',
      animation:'fadeUp 0.5s ease-out both', backdropFilter:'blur(12px)',
      position:'relative', overflow:'hidden',
      boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
      transition:'transform 0.3s ease, box-shadow 0.3s ease',
    },
    statAccent  : { position:'absolute', top:0, left:0, right:0, height:3, borderRadius:'14px 14px 0 0' },
    statIconWrap: { width:46, height:46, borderRadius:12, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:14 },
    statVal     : { fontSize:28, fontWeight:800, color:'#fff', letterSpacing:'-0.5px', animation:'countUp 0.6s ease-out', marginBottom:4 },
    statLabel   : { fontSize:12, color: t.textSub, fontWeight:500 },
    statTrend   : { fontSize:11, fontWeight:600, marginTop:6, display:'flex', alignItems:'center', gap:3 },

    sectionHead : { display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:18 },
    sectionTitle: { fontSize:15, fontWeight:700, color:'#fff', letterSpacing:'-0.1px' },
    sectionSub  : { fontSize:12, color: t.textMuted, marginTop:3 },

    table   : { width:'100%', borderCollapse:'collapse' },
    thead   : { borderBottom: border },
    th      : {
      padding:'9px 14px', textAlign:'left', fontSize:11, fontWeight:700,
      color: t.textMuted,
      textTransform:'uppercase', letterSpacing:0.6,
    },
    td      : {
      padding:'11px 14px', fontSize:13,
      color: t.textSub,
      borderBottom: `1px solid ${t.border}`,
    },
    tdBold  : {
      padding:'11px 14px', fontSize:13, color:'#fff', fontWeight:600,
      borderBottom: `1px solid ${t.border}`,
    },

    badge   : {
      display:'inline-flex', alignItems:'center', padding:'3px 9px',
      borderRadius:6, fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:0.4,
    },

    btnPrimary  : {
      padding:'9px 18px',
      background:'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color:'#fff', border:'none', borderRadius:9, fontSize:13, fontWeight:600,
      cursor:'pointer', boxShadow:'0 3px 12px rgba(0,0,0,0.2)', letterSpacing:0.1,
    },
    btnSecondary: {
      padding:'9px 18px',
      background:'rgba(255,255,255,0.12)',
      color:'#fff',
      border:'1px solid rgba(255,255,255,0.25)',
      borderRadius:9, fontSize:13, fontWeight:600, cursor:'pointer',
      backdropFilter:'blur(8px)',
    },
    btnDanger   : {
      padding:'9px 18px',
      background:'rgba(239,68,68,0.2)',
      color:'#fca5a5',
      border:'1px solid rgba(239,68,68,0.35)',
      borderRadius:9, fontSize:13, fontWeight:600, cursor:'pointer',
    },
    btnSuccess  : {
      padding:'9px 18px',
      background:'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      color:'#fff', border:'none', borderRadius:9, fontSize:13, fontWeight:600, cursor:'pointer',
      boxShadow:'0 3px 12px rgba(16,185,129,0.3)',
    },
    btnIcon     : {
      padding:'8px', background:'rgba(255,255,255,0.12)', color:'#fff',
      border:'1px solid rgba(255,255,255,0.2)', borderRadius:8, fontSize:14,
      cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', width:34, height:34,
    },

    grid4 : { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(155px,1fr))', gap:14 },
    grid3 : { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))', gap:16 },
    grid2 : { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:16 },

    loadWrap: {
      minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center',
      background:'#0d0d14',
      flexDirection:'column', gap:14,
    },
    spinner : {
      width:36, height:36,
      border:'3px solid rgba(255,255,255,0.25)',
      borderTop:'3px solid #fff',
      borderRadius:'50%', animation:'spin 0.75s linear infinite',
    },
    loadText: { fontSize:13, color:'rgba(255,255,255,0.8)' },

    empty    : { textAlign:'center', padding:'52px 20px' },
    emptyIcon: { fontSize:36, marginBottom:10, opacity:0.5 },
    emptyText: { fontSize:14, fontWeight:600, color:'#fff' },
    emptySub : { fontSize:12, color:'rgba(255,255,255,0.6)', marginTop:5 },

    alertErr: {
      padding:'11px 15px',
      background:'rgba(239,68,68,0.2)',
      border:'1px solid rgba(239,68,68,0.35)',
      borderRadius:10, fontSize:13, color:'#fca5a5', marginBottom:16,
    },
    alertOk : {
      padding:'11px 15px',
      background:'rgba(16,185,129,0.2)',
      border:'1px solid rgba(16,185,129,0.35)',
      borderRadius:10, fontSize:13, color:'#6ee7b7', marginBottom:16,
    },

    progressWrap : { marginBottom:12 },
    progressLabel: { display:'flex', justifyContent:'space-between', marginBottom:5 },
    progressTrack: { height:5, background:'rgba(255,255,255,0.15)', borderRadius:3, overflow:'hidden' },
    progressFill : { height:'100%', borderRadius:3, transition:'width 1.2s cubic-bezier(0.4,0,0.2,1)' },

    themeToggle: {
      display:'flex', alignItems:'center', gap:6, padding:'6px 12px',
      background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.25)',
      borderRadius:8, cursor:'pointer', fontSize:12, color:'#fff', fontWeight:600,
      backdropFilter:'blur(8px)',
    },

    // Action blocks
    actionBlock: {
      padding:'24px 20px', borderRadius:16,
      border:'1px solid rgba(255,255,255,0.2)',
      background:'rgba(255,255,255,0.12)',
      cursor:'pointer', display:'flex', flexDirection:'column',
      alignItems:'center', justifyContent:'center', gap:12, textAlign:'center',
      backdropFilter:'blur(12px)',
      boxShadow:'0 4px 20px rgba(0,0,0,0.12)',
      transition:'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
      minHeight: 130,
    },
    actionBlockIcon: {
      width:56, height:56, borderRadius:16,
      display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, marginBottom:4,
    },
    actionBlockLabel: { fontSize:14, fontWeight:700, color:'#fff' },
    actionBlockSub: { fontSize:12, color:'rgba(255,255,255,0.65)' },

    // Form inputs
    input: {
      width:'100%', padding:'10px 14px',
      background:'rgba(255,255,255,0.12)',
      border:'1px solid rgba(255,255,255,0.25)',
      borderRadius:9, fontSize:13, color:'#fff', outline:'none', fontFamily:FONT,
    },
    textarea: {
      width:'100%', padding:'10px 14px',
      background:'rgba(255,255,255,0.12)',
      border:'1px solid rgba(255,255,255,0.25)',
      borderRadius:9, fontSize:13, color:'#fff', outline:'none', fontFamily:FONT, resize:'vertical',
    },
    label: {
      fontSize:12, fontWeight:600, color:'rgba(255,255,255,0.8)',
      marginBottom:6, display:'block', textTransform:'uppercase', letterSpacing:0.5,
    },
    select: {
      width:'100%', padding:'10px 14px',
      background: '#1e1b4b',
      border:'1px solid rgba(255,255,255,0.25)',
      borderRadius:9, fontSize:13, color:'#fff', outline:'none', fontFamily:FONT, cursor:'pointer',
    },
  };
}
